import { useEffect, useState } from 'react'
import { useAuthContext } from '~/src/context/AuthProvider'
import { ResultsResp } from '~/src/db/queryResultsResp'
import useAuthUser from '~/src/hooks/useAuthUser'
import useFetchAnonymousRecentsCount from '~/src/hooks/useFetchAnonymousRecentsCount'
import { fixUrl } from '~/src/lib/helpers'

interface Props {
  resultData?: ResultsResp
  isOwnResults?: boolean
}

const MIN_RECENT_COUNT = 3

const useForceAnonymousAuth = ({ resultData, isOwnResults }: Props) => {
  const { authUser, loading: authUserLoading } = useAuthUser()
  // ⚠️ Only meaningful once auth has resolved. While Firebase is still
  // restoring the session `authUser` is empty, so a signed-in shopper reads as
  // anonymous — and nothing closes the modal once they resolve, because
  // `openAuthModal` only checks for a real account at the moment it opens.
  const isAnonymous = !authUserLoading && !authUser?.uid
  const { openAuthModal, isOpen } = useAuthContext()
  const [closedAuthModal, setClosedAuthModal] = useState(false)

  const mainImage = fixUrl(resultData?.image || resultData?.dataDetected?.image)

  const { data: recentsCountData } = useFetchAnonymousRecentsCount({
    enabled: !!mainImage,
  })
  const recentsCount = recentsCountData?.recentsCount || 0

  useEffect(() => {
    if (isOpen) return
    if (closedAuthModal) return
    if (!isOwnResults) return
    if (!isAnonymous) return
    if (!mainImage) return
    if (recentsCount < MIN_RECENT_COUNT) return

    openAuthModal({
      source: 'anonymous-auth',
      modalTitle: `Sign up to see results`,
      modalSubtitle: 'Never lose a find!',
      fullScreen: false,
      disableClose: recentsCount > MIN_RECENT_COUNT,
      onClose: () => {
        setClosedAuthModal(true)
      },
    })
  }, [
    isOpen,
    isAnonymous,
    openAuthModal,
    recentsCount,
    mainImage,
    isOwnResults,
    closedAuthModal,
  ])
}

export default useForceAnonymousAuth
